<template>
    <div class="table-responsive">
        <loader v-if="isLoading" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>Reference:</b-th>
                    <b-th>Title:</b-th>
                    <b-th>Status:</b-th>
                    <b-th>Total:</b-th>
                    <b-th>Created:</b-th>
                    <b-th>Notes:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody>
                <b-tr v-for="(estimate, i) in list" :key="i">
                    <b-td class="text-nowrap">
                        <estimate-link
                            :estimate="estimate"
                            :useRouter="USE_ESTIMATE_MODULE_VUE"
                        />
                    </b-td>
                    <b-td> {{ estimate.title }} </b-td>
                    <b-td>
                        {{ getStatus(estimate) }}
                    </b-td>
                    <b-td> {{ estimate.total_amount }} </b-td>
                    <b-td> {{ _carbonDateDisplay(estimate.start) }}</b-td>
                    <b-td> {{ estimate.note }}</b-td>
                    <b-td class="text-right">
                        <spinner v-if="estimate.isLoading" />
                        <b-dropdown
                            v-else
                            variant="link"
                            toggle-class="text-decoration-none"
                            no-caret
                            right
                        >
                            <template v-slot:button-content>
                                <i class="cus-cog-down"></i>
                            </template>
                            <b-dropdown-item
                                href="#"
                                @click="goToLink(estimate)"
                            >
                                <i class="cus-page-white-edit"></i>
                                View Estimate
                            </b-dropdown-item>
                            <b-dropdown-item
                                href="#"
                                v-if="
                                    _hasAccess('billing.estimate.approve') ==
                                        1 && estimate.is_approvable
                                "
                                @click="showApproveModal(estimate)"
                            >
                                <i class="cus-accept"></i>
                                Approve Estimate
                            </b-dropdown-item>

                            <b-dropdown-item
                                href="#"
                                v-if="
                                    _hasAccess('billing.estimate.conver') &&
                                    estimate.is_convertable
                                "
                                @click="showConvertModal(estimate)"
                            >
                                <i class="cus-arrow-refresh"></i>
                                Convert Estimate
                            </b-dropdown-item>
                            <b-dropdown-item
                                href="#"
                                @click="showEmailModal(estimate)"
                            >
                                <i class="cus-email"></i>
                                Email Estimate
                            </b-dropdown-item>

                            <b-dropdown-item
                                href="#"
                                @click="printEstimate(estimate)"
                            >
                                <i class="cus-printer"></i>
                                Print Estimate
                            </b-dropdown-item>

                            <b-dropdown-item
                                v-if="
                                    _hasAccess('billing.estimate.sms') &&
                                    (_isExtensionEnabled(
                                        EXTENSIONS.CODE.CODE_SMS
                                    ) ||
                                        _isExtensionEnabled(
                                            EXTENSIONS.CODE.CODE_2_WAY_MESSAGING
                                        ))
                                "
                                href="#"
                                @click="openEstimateSMSModal(estimate)"
                            >
                                <i class="cus-phone"></i>
                                Text (SMS) Estimate
                            </b-dropdown-item>

                            <b-dropdown-item
                                href="#"
                                @click="confirmDelete(estimate)"
                            >
                                <i class="cus-delete"></i>
                                Delete Estimate
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-td>
                </b-tr>
                <b-tr v-if="list.length == 0 && !isLoading">
                    <b-td colspan="6"><i>No records available</i></b-td>
                </b-tr>
            </b-tbody>
        </b-table-simple>

        <b-pagination
            v-if="meta"
            v-show="meta.lastPage > 1"
            class="vet-pagination"
            align="right"
            v-model="meta.currentPage"
            :total-rows="meta.total"
            :per-page="meta.perPage"
            prev-text="Prev"
            next-text="Next"
            :hide-goto-end-buttons="true"
            @change="getList"
        />

        <modal-delete
            modal-id="modal-delete-estimate"
            :estimate="selectedEstimate"
            @reload="getList()"
        />
        <modal-approve
            modal-ref="modal-approve-estimate"
            ref="modal-approve-estimate"
            :client="clientDetails"
            :estimate="selectedEstimate"
            @reload="getList()"
        />
        <modal-convert
            modal-ref="modal-convert-estimate"
            ref="modal-convert-estimate"
            :client="clientDetails"
            :estimate="selectedEstimate"
            @reload="getList()"
        />
        <modal-email
            modal-ref="modal-email-estimate"
            ref="modal-email-estimate"
            :estimate="selectedEstimate"
            :client="clientDetails"
            @reload="getList()"
            @show-loading="
                isLoading => {
                    selectedEstimate.isLoading = isLoading;
                }
            "
        />
        <exportPrintPdf
            ref="exportPrintPdf"
            :estimate="selectedEstimate"
            :client="clientDetails"
            @show-loading="
                isLoading => {
                    selectedEstimate.isLoading = isLoading;
                }
            "
        />
        <estimate-send-SMS-modal ref="modal-estimate-send-sms" />
    </div>
</template>
<script>
import Billings from "@/services/client-billings";
import Loader from "@/components/elements/loader-list";
import CBILLINGS from "@/constants/billings";
import EstimateLink from "@/components/commons/estimate-link";
import BillingEstimateServices from "@/services/billing/billing-estimate.js";
import EXTENSIONS from "@/constants/extensions";

export default {
    components: {
        Loader,
        EstimateLink,
        ModalDelete: () =>
            import("@/components/billing-estimate/estimate-delete-modal"),
        ModalApprove: () =>
            import("@/components/billing-estimate/estimate-approve-modal"),
        ModalConvert: () =>
            import("@/components/billing-estimate/estimate-convert-modal"),
        ModalEmail: () =>
            import("@/components/billing-estimate/estimate-email-modal"),
        Spinner: () => import("@/components/elements/spinner"),
        ExportPrintPdf: () =>
            import("@/components/billing-export/export-print-pdf.vue"),
        EstimateSendSMSModal: () =>
            import("@/components/estimate-sms/modal-estimate-send-sms")
    },
    data() {
        return {
            meta: null,
            list: [],
            isLoading: false,
            selectedEstimate: { name: null },
            componentKey: 0,
            EXTENSIONS
        };
    },
    computed: {
        clientId() {
            return this.$store.getters["client/clientId"];
        },
        clientDetails() {
            return this.$store.getters["client/clientDetails"];
        }
    },
    mounted() {
        this.getList();
    },
    methods: {
        getList(page) {
            this.isLoading = true;
            let params = {
                page: page ? page : 1,
                perPage: this.PER_PAGE,
                type: CBILLINGS.BILLING_TYPE.ESTIMATES
            };

            Billings.getBillings(this.clientId, params)
                .then(response => {
                    this.list = response.resources.map(estimate => {
                        estimate.isLoading = false;
                        return estimate;
                    });
                    this.meta = response.meta;
                    this.isLoading = false;
                })
                .catch(error => {
                    this._message(
                        "Error : Unable to get estimate list",
                        "danger"
                    );
                    this.isLoading = false;
                    console.error(error);
                });
        },
        goToLink(estimate) {
            if (
                this.USE_ESTIMATE_MODULE_VUE ||
                this.$store.getters["useVueEstimate"]
            ) {
                this.$router.push({
                    name: "estimate",
                    params: { id: estimate.encoded_id }
                });
            } else {
                window.location.href = process.env.CI_APPS + estimate.ci_url;
            }
        },
        getStatus(estimate) {
            let status = "Open";

            if (estimate.isapprove == 1) {
                if (estimate.isconvert == 1) {
                    status =
                        "Converted (" +
                        this._carbonDateDisplay(estimate.convert_at) +
                        ")";
                } else {
                    status =
                        "Approved (" +
                        this._carbonDateDisplay(estimate.approve_at) +
                        ")";
                }
            }

            return status;
        },
        confirmDelete(estimate) {
            this.selectedEstimate = estimate;
            this.$bvModal.show("modal-delete-estimate");
        },
        async showApproveModal(estimate) {
            this.selectedEstimate = estimate;
            await this.$nextTick();
            this.$refs["modal-approve-estimate"].open();
        },
        showConvertModal(estimate) {
            this.selectedEstimate = estimate;
            this.$refs["modal-convert-estimate"].open();
        },
        async showEmailModal(estimate) {
            this.selectedEstimate = estimate;
            await this.$nextTick();
            this.$refs["modal-email-estimate"].open();
        },
        async printEstimate(estimate) {
            let self = this;
            self.selectedEstimate = estimate;
            await this.$nextTick();
            self.$refs.exportPrintPdf.init();
        },
        openEstimateSMSModal(estimate) {
            this.selectedEstimate = estimate;
            this.$refs["modal-estimate-send-sms"].open(
                this.selectedEstimate.id
            );
        }
    }
};
</script>
<style type="text/css">
.refund-icon {
    margin: 6px 12px;
    cursor: pointer;
}
</style>
